<script setup lang="ts">
import { type TraderDetail, type TraderDepositWithdrawalTab } from '~/server/detail'

interface DepositWithdrawalFiat {
  code?: string
  name?: string
  flag?: string
  minAmount?: string
  paymentMethod?: string
}

interface DepositWithdrawalTradeItem {
  tradeType?: string
  fiats?: DepositWithdrawalFiat[] | null
}

interface DepositWithdrawalTab extends TraderDepositWithdrawalTab {
  type?: number
  list?: DepositWithdrawalTradeItem[] | null
}

interface DepositWithdrawalSection {
  type?: number
  tabs?: DepositWithdrawalTab[] | null
}

interface FundingTypeOption {
  key: number
  label: string
}

interface FundingCardGroup {
  key: number
  label: string
  cards: FundingDisplayCard[]
}

interface FundingDisplayCard {
  key: string
  fundingType: string
  tradeType: string
  fiats: DepositWithdrawalFiat[]
}

const { t } = useAppI18n()
const matomo = useMatomo()

type FundingDirectionKey = 'withdraw' | 'deposit'

type FundingDirectionTab = {
  key: FundingDirectionKey
  label: string
  type: number
}

const props = withDefaults(
  defineProps<{
    traderData?: TraderDetail | null
  }>(),
  {
    traderData: null,
  },
)

const activeTab = ref<FundingDirectionKey>('withdraw')
const activeFundingTypeMap = ref<Record<FundingDirectionKey, number | null>>({
  withdraw: null,
  deposit: null,
})
const activeFundingType = computed({
  get: () => activeFundingTypeMap.value[activeTab.value],
  set: (value: number | null) => {
    activeFundingTypeMap.value[activeTab.value] = value
  },
})
const selectedFiatIndexMap = ref<Record<string, number>>({})
const openFiatMenuCardKey = ref('')
const hoveredFundingMethodCardKey = ref('')
let fundingMethodLeaveTimer: ReturnType<typeof window.setTimeout> | null = null
const tabs = computed(() => [
  {
    key: 'withdraw' as const,
    label: t('82676'),
    type: 0,
  },
  {
    key: 'deposit' as const,
    label: t('82677'),
    type: 1,
  },
])

/** 直接使用接口当前返回的 bitDepositAndWithdrawal 数组。 */
const depositWithdrawalSections = computed<DepositWithdrawalSection[]>(() => {
  return Array.isArray(props.traderData?.bitDepositAndWithdrawal)
    ? (props.traderData.bitDepositAndWithdrawal as DepositWithdrawalSection[])
    : []
})

/** 接口未返回出入金数据时，整个模块不展示。 */
const shouldShowDepositWithdrawal = computed(() => props.traderData?.bitDepositAndWithdrawal !== null)

const activeDirectionType = computed(() => {
  return tabs.value.find((tab) => tab.key === activeTab.value)?.type ?? 0
})

const activeDepositWithdrawalSection = computed<DepositWithdrawalSection | null>(() => {
  return depositWithdrawalSections.value.find((section) => section.type === activeDirectionType.value) || null
})

/** 当前入金/出金下的 tabs 直接作为头部类型切换。 */
const activeFundingTabs = computed<DepositWithdrawalTab[]>(() => {
  return activeDepositWithdrawalSection.value?.tabs || []
})

/** tabs 大于 1 时展示头部 tab，卡片直接遍历对应 tab.list。 */
const fundingCardGroups = computed<FundingCardGroup[]>(() => {
  return activeFundingTabs.value.map((tab, tabIndex) => ({
    key: tab.type ?? tabIndex,
    label: tab.title || '',
    cards: flattenFundingCards(tab, `${activeDirectionType.value}-${tab.type ?? tabIndex}`),
  }))
})

const shouldShowFundingTypeTabs = computed(() => fundingCardGroups.value.length > 1)

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const currentDirectionTab = computed(() => tabs.value.find((tab) => tab.key === activeTab.value) || tabs.value[0])

const getExchangeTrackPayload = () => ({
  af_name:
    normalizeText(props.traderData?.basicInfo?.showName) ||
    normalizeText(props.traderData?.basicInfo?.localShortName) ||
    normalizeText(props.traderData?.basicInfo?.shortName),
  af_code: normalizeText(props.traderData?.basicInfo?.traderCode),
})

/** 切换出入金 tab 时上报当前交易所和 tab 信息。 */
const handleDirectionTabClick = (tab: FundingDirectionTab) => {
  activeTab.value = tab.key
  matomo.trackEvent('broker_rechange_tab', {
    af_type: tab.label,
    af_type1: tab.type,
    ...getExchangeTrackPayload(),
  })
}

const trackBrokerRechangeClick = (afKind: 'tab' | '筛选') => {
  const tab = currentDirectionTab.value
  matomo.trackEvent('broker_rechange_click', {
    af_type: tab.label,
    af_type1: tab.type,
    af_kind: afKind,
    ...getExchangeTrackPayload(),
  })
}

const fundingTypeOptions = computed<FundingTypeOption[]>(() => {
  return fundingCardGroups.value.map((group) => ({
    key: group.key,
    label: group.label,
  }))
})

const displayCards = computed<FundingDisplayCard[]>(() => {
  if (typeof activeFundingType.value !== 'number') {
    return fundingCardGroups.value.flatMap((group) => group.cards)
  }

  return fundingCardGroups.value.find((group) => group.key === activeFundingType.value)?.cards || []
})

/** 给每张卡生成唯一 key，避免切换其他 tab 时共用同一份法币选中状态。 */
function flattenFundingCards(tab: DepositWithdrawalTab, keyPrefix: string): FundingDisplayCard[] {
  return (tab.list || []).map((tradeItem, tradeIndex) => ({
    key: `${keyPrefix}-${tab.title || 'funding'}-${tradeItem.tradeType || 'trade'}-${tradeIndex}`,
    fundingType: tab.title || '',
    tradeType: tradeItem.tradeType || '',
    fiats: tradeItem.fiats || [],
  }))
}

watch(
  displayCards,
  (nextCards) => {
    if (!nextCards.length) {
      hoveredFundingMethodCardKey.value = ''
      return
    }

    const nextSelectedFiatIndexMap = { ...selectedFiatIndexMap.value }

    nextCards.forEach((card) => {
      const currentIndex = selectedFiatIndexMap.value[card.key] ?? 0
      const maxIndex = Math.max(card.fiats.length - 1, 0)
      nextSelectedFiatIndexMap[card.key] = Math.min(currentIndex, maxIndex)
    })

    selectedFiatIndexMap.value = nextSelectedFiatIndexMap
  },
  {
    immediate: true,
  },
)

watch(
  fundingCardGroups,
  (nextGroups) => {
    if (!nextGroups.length) {
      activeFundingType.value = null
      return
    }

    if (!nextGroups.some((group) => group.key === activeFundingType.value)) {
      activeFundingType.value = nextGroups[0]?.key ?? 0
    }
  },
  {
    immediate: true,
  },
)

/** 切换当前展示的交易类型卡片分组。 */
const handleFundingTypeChange = (key: FundingTypeOption['key']) => {
  activeFundingType.value = key
  trackBrokerRechangeClick('tab')
}

/** 读取当前卡片已选中的法币，默认取第一个。 */
const getSelectedFiat = (card: FundingDisplayCard) => {
  const selectedIndex = selectedFiatIndexMap.value[card.key] ?? 0

  return card.fiats[selectedIndex] || null
}

/** 更新当前卡片选中的法币。 */
const handleFiatChange = (cardKey: string, fiatIndex: number) => {
  selectedFiatIndexMap.value = {
    ...selectedFiatIndexMap.value,
    [cardKey]: fiatIndex,
  }

  trackBrokerRechangeClick('筛选')
  openFiatMenuCardKey.value = ''
}

/** hover 或 focus 到超出两行的支付方式时，展示完整内容弹层。 */
const handleFundingMethodEnter = (cardKey: string, event: MouseEvent | FocusEvent) => {
  if (fundingMethodLeaveTimer) {
    window.clearTimeout(fundingMethodLeaveTimer)
    fundingMethodLeaveTimer = null
  }

  const currentTarget = event.currentTarget

  if (!(currentTarget instanceof HTMLElement)) {
    return
  }

  const methodsElement = currentTarget.classList.contains('funding-methods')
    ? currentTarget
    : currentTarget.querySelector<HTMLElement>('.funding-methods')

  if (!methodsElement || methodsElement.scrollHeight - methodsElement.clientHeight <= 1) {
    hoveredFundingMethodCardKey.value = ''
    return
  }

  hoveredFundingMethodCardKey.value = cardKey
}

/** 鼠标移出或失焦后关闭支付方式弹层。 */
const handleFundingMethodLeave = (cardKey: string) => {
  if (hoveredFundingMethodCardKey.value !== cardKey) {
    return
  }

  fundingMethodLeaveTimer = window.setTimeout(() => {
    if (hoveredFundingMethodCardKey.value === cardKey) {
      hoveredFundingMethodCardKey.value = ''
    }

    fundingMethodLeaveTimer = null
  }, 120)
}

/** 切换当前卡片的法币下拉菜单。 */
const toggleFiatMenu = (cardKey: string) => {
  openFiatMenuCardKey.value = openFiatMenuCardKey.value === cardKey ? '' : cardKey
}

/** 点击空白处时关闭已展开的法币菜单。 */
const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target

  if (target instanceof Element && target.closest('.funding-currency-select')) {
    return
  }

  openFiatMenuCardKey.value = ''
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  if (fundingMethodLeaveTimer) {
    window.clearTimeout(fundingMethodLeaveTimer)
    fundingMethodLeaveTimer = null
  }

  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <section v-if="shouldShowDepositWithdrawal" class="deposit-withdrawal-card" :aria-label="t('82612')">
    <div class="funding-interaction-surface">
      <header class="deposit-withdrawal-tabs" role="tablist" :aria-label="t('82612')">
        <h2 id="deposit-withdrawal-title" class="sr-only">{{ t('82612') }}</h2>
        <button
          v-for="tab in tabs"
          :id="`deposit-withdrawal-tab-${tab.key}`"
          :key="tab.key"
          type="button"
          class="deposit-withdrawal-tab"
          :class="{ 'deposit-withdrawal-tab-active': activeTab === tab.key }"
          role="tab"
          :aria-selected="activeTab === tab.key"
          :aria-controls="`deposit-withdrawal-panel-${tab.key}`"
          @click="handleDirectionTabClick(tab)"
        >
          <span class="tab-label">{{ tab.label }}</span>
          <span v-if="activeTab === tab.key" class="tab-track" aria-hidden="true"></span>
        </button>
      </header>

      <div
        :id="`deposit-withdrawal-panel-${activeTab}`"
        class="deposit-withdrawal-panel"
        role="tabpanel"
        :aria-labelledby="`deposit-withdrawal-tab-${activeTab}`"
      >
        <div :key="activeTab" class="funding-variant-block">
          <div v-if="shouldShowFundingTypeTabs" class="funding-toolbar">
            <div class="funding-type-tabs" role="tablist" :aria-label="t('82581')">
              <button
                v-for="option in fundingTypeOptions"
                :id="`funding-type-tab-${option.key}`"
                :key="option.key"
                type="button"
                class="funding-type-tab"
                :class="{ 'funding-type-tab-active': activeFundingType === option.key }"
                role="tab"
                :aria-selected="activeFundingType === option.key"
                :aria-controls="`funding-type-panel-${option.key}`"
                @click="handleFundingTypeChange(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            :id="`funding-type-panel-${activeFundingType}`"
            class="funding-card-list"
            :class="{
              'funding-card-list-without-tabs': !shouldShowFundingTypeTabs,
              'funding-card-list-single': displayCards.length === 1,
              'funding-card-list-double': displayCards.length === 2,
            }"
            role="tabpanel"
            :aria-labelledby="`funding-type-tab-${activeFundingType}`"
            data-exchange-detail-section="deposit-withdrawal-advanced"
          >
            <template v-if="displayCards.length">
              <article v-for="card in displayCards" :key="card.key" class="funding-info-card">
                <div v-if="!shouldShowFundingTypeTabs && card.fundingType" class="funding-row">
                  <span class="funding-label">{{ t('82581') }}</span>
                  <span class="funding-value">{{ card.fundingType }}</span>
                </div>

                <div v-if="card.tradeType" class="funding-row">
                  <span class="funding-label">{{ t('82681') }}</span>
                  <span class="funding-value">{{ card.tradeType }}</span>
                </div>

                <div v-if="card.fiats.length" class="funding-row">
                  <span class="funding-label">{{ t('82682') }}</span>
                  <span class="funding-currency">
                    <div class="funding-currency-select">
                      <button
                        type="button"
                        class="funding-select-button"
                        aria-haspopup="listbox"
                        :aria-expanded="openFiatMenuCardKey === card.key"
                        @click.stop="toggleFiatMenu(card.key)"
                      >
                        <CommonImage
                          v-if="getSelectedFiat(card)?.flag"
                          class="funding-select-flag"
                          :src="getSelectedFiat(card)?.flag"
                          alt=""
                          width="18"
                          height="12"
                          loading="lazy"
                        />
                        <span class="funding-select-text">{{ getSelectedFiat(card)?.name }}</span>
                        <CommonImage
                          class="funding-select-arrow"
                          :class="{ 'funding-select-arrow-open': openFiatMenuCardKey === card.key }"
                          src="https://resources1.interface003.com/web30/2026/06/18/be4d2db9-7582-451c-be53-fd038c521371.png"
                          alt=""
                          width="16"
                          height="16"
                          aria-hidden="true"
                        />
                      </button>

                      <div
                        v-if="openFiatMenuCardKey === card.key"
                        class="funding-select-menu"
                        role="listbox"
                        aria-label="支持法币"
                      >
                        <button
                          v-for="(fiat, fiatIndex) in card.fiats"
                          :key="`${card.key}-${fiat.code || fiat.name || fiatIndex}`"
                          type="button"
                          class="funding-select-option"
                          :class="{
                            'funding-select-option-active': (selectedFiatIndexMap[card.key] ?? 0) === fiatIndex,
                          }"
                          role="option"
                          :aria-selected="(selectedFiatIndexMap[card.key] ?? 0) === fiatIndex"
                          @click.stop="handleFiatChange(card.key, fiatIndex)"
                        >
                          <CommonImage
                            v-if="fiat.flag"
                            class="funding-select-flag"
                            :src="fiat.flag"
                            alt=""
                            width="18"
                            height="12"
                            loading="lazy"
                          />
                          <span class="funding-select-text">{{ fiat.name }}</span>
                        </button>
                      </div>
                    </div>
                  </span>
                </div>

                <div v-if="getSelectedFiat(card)?.minAmount" class="funding-row">
                  <span class="funding-label">{{ t('82683') }}</span>
                  <span class="funding-value">{{ getSelectedFiat(card)?.minAmount }}</span>
                </div>

                <div v-if="getSelectedFiat(card)?.paymentMethod" class="funding-row funding-row-methods">
                  <span class="funding-label">{{ t('82684') }}</span>
                  <span
                    class="funding-methods-wrapper"
                    @mouseenter="handleFundingMethodEnter(card.key, $event)"
                    @mouseleave="handleFundingMethodLeave(card.key)"
                  >
                    <span
                      class="funding-methods"
                      tabindex="0"
                      @focus="handleFundingMethodEnter(card.key, $event)"
                      @blur="handleFundingMethodLeave(card.key)"
                    >
                      {{ getSelectedFiat(card)?.paymentMethod }}
                    </span>
                    <span
                      v-if="hoveredFundingMethodCardKey === card.key"
                      class="funding-methods-tooltip"
                      role="tooltip"
                    >
                      <span class="funding-methods-tooltip-content">
                        {{ getSelectedFiat(card)?.paymentMethod }}
                      </span>
                    </span>
                  </span>
                </div>
              </article>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.deposit-withdrawal-card {
  display: flex;
  width: 100%;
  flex-direction: column;
  color: #292a2e;
}

.funding-interaction-surface {
  width: 100%;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 1px var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  padding: 24px;
}

.deposit-withdrawal-tabs {
  display: flex;
  width: 100%;
  height: 44px;
  align-items: stretch;
  gap: 24px;
  box-shadow: inset 0 -0.5px 0 var(--color-border-default, rgba(5, 21, 36, 0.06));
}

.deposit-withdrawal-tab {
  display: flex;
  height: 44px;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border: 0;
  appearance: none;
  background: transparent;
  color: #8c8f97;
  cursor: pointer;
  font: inherit;
  padding: 0;
}

.deposit-withdrawal-tab-active {
  color: #292a2e;
  font-weight: 600;
}

.tab-label {
  display: block;
  font-size: 20px;
  letter-spacing: 0;
  line-height: 28px;
  white-space: nowrap;
  word-break: break-word;
}

.tab-track {
  display: block;
  width: 28px;
  height: 4px;
  border-radius: 999px;
  background: #292a2e;
}

.deposit-withdrawal-panel {
  width: 100%;
}

.funding-variant-block + .funding-variant-block {
  margin-top: 24px;
}

.funding-toolbar {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
}

.funding-type-tabs {
  display: flex;
  min-width: 0;
  flex: 1 0 0;
  align-items: flex-start;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.funding-type-tabs::-webkit-scrollbar {
  display: none;
}

.funding-type-tab {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 2px;
  background: #fff;
  color: #292a2e;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  padding: 4px 12px;
  white-space: nowrap;
}

.funding-type-tab-active {
  background: #112d73;
  color: #fff;
  font-weight: 500;
}

.funding-card-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  gap: 24px;
  width: 100%;
}

.funding-card-list-single {
  grid-template-columns: minmax(0, 1fr);
}

.funding-card-list-double {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.funding-card-list-without-tabs {
  padding-top: 20px;
}

.funding-info-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
  border: 1px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  border-radius: 8px;
  padding: 16px;
}

.funding-row {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 8px;
  box-shadow: inset 0 -0.5px 0 var(--color-border-default, rgba(5, 21, 36, 0.06));
  padding-bottom: 12px;
  color: #292a2e;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 22px;
  word-break: break-word;
}

.funding-row-methods {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  box-shadow: none;
}

.funding-row:last-child {
  box-shadow: none;
  padding-bottom: 0;
}

.funding-label {
  flex: 1 0 0;
  min-width: 0;
  color: #7d818a;
  font-weight: 400;
}

.funding-value {
  min-width: 0;
  text-align: right;
  font-weight: 500;
}

.funding-currency {
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  font-weight: 500;
  white-space: nowrap;
}

.funding-currency-select {
  position: relative;
  flex-shrink: 0;
}

.funding-select-button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 0;
  border-radius: 2px;
  background: #fff;
  color: #292a2e;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
  min-height: 24px;
}

.funding-select-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.funding-select-arrow-open {
  transform: rotate(180deg);
}

[dir='rtl'] .funding-select-arrow {
  margin-right: 4px;
  margin-left: 0;
}

.funding-select-menu {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  right: 0;
  min-width: 100%;
  max-height: 150px;
  overflow-y: auto;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 2px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(5, 21, 36, 0.08);
  padding: 4px;
  font-weight: 400;
  scrollbar-width: none;
}

.funding-select-menu::-webkit-scrollbar {
  display: none;
}

.funding-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  border-radius: 2px;
  background: transparent;
  color: #292a2e;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  line-height: 22px;
  padding: 4px 6px;
  text-align: left;
  white-space: nowrap;
}

.funding-select-option-active {
  color: #1868db;
}

.funding-select-option:hover {
  background: #f8f8f8;
}

.funding-select-flag,
.funding-flag {
  width: 18px;
  height: 12px;
  flex-shrink: 0;
  object-fit: cover;
}

.funding-select-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.funding-select-button .funding-select-flag {
  margin-right: 8px;
}

[dir='rtl'] .funding-select-button .funding-select-flag {
  margin-right: 0;
  margin-left: 8px;
}

.funding-methods {
  width: 100%;
  display: -webkit-box;
  overflow: hidden;
  color: #292a2e;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.funding-methods-wrapper {
  position: relative;
  width: 100%;
}

.funding-methods-wrapper-hoverable .funding-methods {
  cursor: pointer;
}

.funding-methods-tooltip {
  position: absolute;
  z-index: 30;
  top: calc(100% + 8px);
  left: 0;
  width: min(336px, calc(100vw - 64px));
  max-height: 134px;
  overflow: visible;
  box-sizing: border-box;
  border-radius: 4px;
  background: #fff;
  box-shadow:
    0 0 1px rgba(0, 0, 0, 0.2),
    0 8px 20px rgba(0, 0, 0, 0.12);
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  padding: 12px;
  pointer-events: auto;
  word-break: break-word;
}

.funding-methods-tooltip-content {
  display: block;
  max-height: 110px;
  overflow-y: auto;
  scrollbar-width: none;
  word-break: break-word;
}

.funding-methods-tooltip-content::-webkit-scrollbar {
  display: none;
}

.funding-methods-tooltip::before {
  position: absolute;
  top: -6px;
  left: 50%;
  width: 24px;
  height: 6px;
  background: rgba(0, 0, 0, 0.08);
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  content: '';
  transform: translateX(-50%);
}

.funding-methods-tooltip::after {
  position: absolute;
  top: -5px;
  left: 50%;
  width: 22px;
  height: 5px;
  background: #fff;
  clip-path: polygon(50% 0, 100% 100%, 0 100%);
  content: '';
  transform: translateX(-50%);
}

[dir='rtl'] .funding-methods-tooltip {
  right: 0;
  left: auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media screen and (max-width: 799px) {
  .deposit-withdrawal-card {
    padding: 20px 16px;
  }

  .funding-toolbar {
    align-items: flex-start;
  }

  .funding-card-list {
    grid-template-columns: 1fr;
  }
}
</style>
